// ============================================================
// v4 — the card catalog + per-game deck configuration. As of v8
// (BOLT-007) the catalog is DERIVED from the core Pack (packs/core
// → CORE_DECK), the single source for the 28 shipped cards, so no
// copy is duplicated. `count` means:
//   • hider cards  → copies in the shuffled deck (draw frequency)
//   • seeker cards → plays allowed per round (default 1)
//
// The deck-config types (CardDeck/CardSetting/DeckConfig) live in
// pack.ts now; re-exported here so existing `from './cards'`
// imports keep working (full spec §5).
// ============================================================
import { CORE_DECK } from './packs/core';
import type { CardDeck } from './pack';

export type { CardDeck, CardSetting, DeckConfig } from './pack';

export interface CatalogEntry {
  id: string; // stable per card TYPE (hider) or card (seeker)
  deck: CardDeck;
  label: string;
  description: string;
  defaultCount: number;
}

/** The shipped catalog, derived from the core Pack (CORE_DECK). */
export const CARD_CATALOG: CatalogEntry[] = CORE_DECK.map((c) => ({
  id: c.id,
  deck: c.deck,
  label: c.label,
  description: c.description,
  defaultCount: c.defaultCount,
}));

const MAX_COUNT = 20;

const CATALOG_IDS = new Set(CARD_CATALOG.map((c) => c.id));

/** Resolve a card's effective count under a deck config (off ⇒ 0; clamped). */
export function resolveCount(cfg: import('./pack').DeckConfig | undefined, id: string, def: number): number {
  const c = cfg?.[id];
  if (!c) return def;
  if (c.enabled === false) return 0;
  return Math.max(0, Math.min(MAX_COUNT, Math.round(c.count)));
}

/** Validate + clamp a deck config (server-authoritative). Drops unknown ids. */
export function sanitizeDeckConfig(raw: unknown): import('./pack').DeckConfig {
  const out: import('./pack').DeckConfig = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!CATALOG_IDS.has(id) || !v || typeof v !== 'object') continue;
    const o = v as { enabled?: unknown; count?: unknown };
    const o2 = v as { cost?: unknown };
    out[id] = {
      enabled: o.enabled !== false,
      count: typeof o.count === 'number' && Number.isFinite(o.count) ? Math.max(0, Math.min(MAX_COUNT, Math.round(o.count))) : 1,
      // v29 BOLT-165: the cost override. Clamped like the count, and a FLOOR OF
      // ONE: a zero-cost question pays the hider nothing, which quietly removes
      // the whole reason a seeker hesitates - the economy is the game.
      ...(typeof o2.cost === 'number' && Number.isFinite(o2.cost)
        ? { cost: Math.max(1, Math.min(MAX_COUNT, Math.round(o2.cost))) }
        : {}),
    };
  }
  return out;
}

/**
 * v29 BOLT-165 — the cost a seeker card actually charges this game.
 *
 * Mirrors `resolveCount`: the host's override if they set one, else the
 * catalog's auto-costed default. ONE place, so the lobby stepper, the rail
 * badge and the server's payment can never disagree about the number.
 */
export function resolveCost(cfg: import('./pack').DeckConfig | undefined, id: string, fallback: number): number {
  const c = cfg?.[id]?.cost;
  return typeof c === 'number' && Number.isFinite(c) ? c : fallback;
}

/** At least one seeker card must be playable, else the seeker can't play. */
export function seekerEnabledCount(cfg: import('./pack').DeckConfig | undefined): number {
  return CARD_CATALOG.filter((c) => c.deck === 'seeker' && resolveCount(cfg, c.id, c.defaultCount) > 0).length;
}
